import { Container } from "react-bootstrap";
import { motion } from "framer-motion";
import { ShieldCheck, Database, Lock, Eye } from "react-bootstrap-icons";
import { Seo } from "../components/Seo";
import { generateStaticPageSEO } from "../lib/seo/metaGenerators";

export function Privacy() {
  const sections = [
    { icon: ShieldCheck, title: "Information We Collect", content: [
      "Personal information (name, email, address)",
      "Payment information",
      "Order history and preferences",
      "Device and location information",
      "Communication preferences",
    ]},
    { icon: Database, title: "How We Use Your Information", content: [
      "Process and deliver your orders",
      "Improve our services",
      "Send you updates and promotions",
      "Respond to your inquiries",
      "Ensure platform security",
    ]},
    { icon: Lock, title: "Data Protection", content: [
      "Industry-standard encryption",
      "Secure payment processing",
      "Regular security audits",
      "Limited access to personal data",
      "Data retention policies",
    ]},
    { icon: Eye, title: "Your Rights", content: [
      "Access your personal data",
      "Correct inaccurate data",
      "Request data deletion",
      "Opt-out of marketing",
      "Data portability",
    ]}
  ];

  const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.12 } } };
  const itemVariants = { hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0, transition: { duration: 0.42 } } };

  return (
    <>
    <Seo {...generateStaticPageSEO({
      title: "Privacy Policy",
      description: "Read Teco's privacy policy to understand how we collect, use, and protect your personal information.",
      path: "/privacy",
    })} />
    <Container as="section" className="privacy-shell" aria-label="Privacy Policy">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={containerVariants} className="privacy-inner">

        <motion.header variants={itemVariants} className="privacy-head">
          <h1 className="privacy-title">Privacy Policy</h1>
          <p className="privacy-sub micro">Last updated: {new Date().toLocaleDateString()}</p>
        </motion.header>

        <motion.div variants={itemVariants} className="cards-grid">
          {sections.map((s, idx) => {
            const Icon = s.icon;
            return (
              <article className="privacy-card" key={idx} aria-labelledby={`privacy-${idx}`}>
                <motion.div className="card-inner" variants={itemVariants}>
                  <div className="icon-plate"><Icon className="icon-line" /></div>
                  <div className="card-body">
                    <h3 id={`privacy-${idx}`} className="card-title">{s.title}</h3>
                    <ul className="card-list">
                      {s.content.map((c, i) => (
                        <li key={i} className="card-item">
                          <span className="dot" aria-hidden></span>
                          <span className="item-text">{c}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              </article>
            );
          })}
        </motion.div>

        <motion.footer variants={itemVariants} className="privacy-contact">
          <div className="contact-card">
            <h3 className="contact-title">Contact Us</h3>
            <p className="contact-text micro">Questions about this policy? Email us at <a href="mailto:privacy@tecofood.com" className="link">privacy@tecofood.com</a></p>
          </div>
        </motion.footer>
      </motion.div>

      <style>{`
        :root{--maxw:980px;--gap:12px;--radius:12px;--muted:0,0,0;--accent:26,115,232}
        .privacy-shell{padding:20px 12px}
        .privacy-inner{max-width:var(--maxw);margin:0 auto;display:flex;flex-direction:column;gap:18px}

        .privacy-head{text-align:center}
        .privacy-title{margin:0;font-size:22px;font-weight:800;color:rgba(var(--muted),0.92)}
        .privacy-sub{margin:0;color:rgba(0,0,0,0.6);font-size:13px}

        /* Grid: 1 col mobile, 2 cols medium */
        .cards-grid{display:grid;grid-template-columns:1fr;gap:12px;align-items:stretch}
        @media(min-width:720px){.cards-grid{grid-template-columns:repeat(2,1fr);gap:16px}}

        .privacy-card{display:flex;min-height:100%}
        .card-inner{display:flex;flex:1;gap:12px;align-items:flex-start;padding:14px;border-radius:calc(var(--radius) + 2px);background:rgba(255,255,255,0.98);border:1px solid rgba(0,0,0,0.06);transition:transform 160ms cubic-bezier(.2,.9,.2,1),box-shadow 160ms cubic-bezier(.2,.9,.2,1)}

        .icon-plate{flex:0 0 44px;height:44px;border-radius:10px;display:flex;align-items:center;justify-content:center;background:rgba(var(--accent),0.06)}
        .icon-line{width:20px;height:20px;stroke:rgba(0,0,0,0.72);stroke-width:1.2}

        .card-title{margin:0;font-size:15px;font-weight:700}
        .card-list{margin:8px 0 0;padding:0;list-style:none}
        .card-item{display:flex;align-items:flex-start;gap:10px;padding:6px 0}
        .dot{width:6px;height:6px;background:rgba(var(--accent),1);border-radius:50%;flex:0 0 6px;margin-top:6px}
        .item-text{font-size:13px;color:rgba(0,0,0,0.72);line-height:18px}

        .privacy-card:hover .card-inner{transform:translateY(-4px);box-shadow:0 12px 34px rgba(11,22,40,0.04)}

        .privacy-contact .contact-card{padding:14px;border-radius:14px;background:rgba(255,255,255,0.98);border:1px solid rgba(0,0,0,0.05);text-align:center}
        .contact-title{margin:0;font-size:16px;font-weight:700}
        .contact-text{margin:0;color:rgba(0,0,0,0.66)}
        .link{color:rgba(0,0,0,0.7);text-decoration:none}
        .link:hover{color:rgba(var(--accent),1)}

        @media(prefers-reduced-motion:reduce){.card-inner,.privacy-inner{transition:none!important}}
        .privacy-inner,.card-inner{transform:translateZ(0);backface-visibility:hidden}

        @media(max-width:420px){.privacy-title{font-size:20px}.icon-plate{flex:0 0 40px;height:40px}.item-text{font-size:13px}}
      `}</style>
    </Container>
    </>
  );
}
